import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import Header from "../components/Header";
import Footer from "../components/Footer";
import UserAvatar from "../components/UserAvatar";
import api from "../api";
import { getServiceAvailablePlaces } from "../utils/serviceUtils";

const isServiceActive = (s) =>
  s?.isActive !== false &&
  s?.active !== false &&
  s?.desactive !== true &&
  s?.status !== "inactive";

const CreatorsList = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [services, setServices] = useState([]);
  const [users, setUsers] = useState([]);
  const [search, setSearch] = useState("");

  useEffect(() => {
    const load = async () => {
      try {
        const servicesRes = await api.get("/api/services");
        const raw = servicesRes.data || [];

        const enriched = await Promise.all(
          raw.map(async (s) => {
            try {
              const countRes = await api.get(`/api/reservations/count/${s._id}`);
              const backendRemaining = Number(s?.placesRestantes);
              const totalPlaces = Number.isFinite(backendRemaining) && backendRemaining >= 0
                ? backendRemaining
                : Number(getServiceAvailablePlaces(s) || 1);
              const placesRestantes = Number.isFinite(backendRemaining) && backendRemaining >= 0
                ? backendRemaining
                : Math.max(totalPlaces - (countRes.data.count || 0), 0);
              return { ...s, placesRestantes };
            } catch {
              return { ...s, placesRestantes: 1 };
            }
          })
        );

        setServices(enriched.filter((s) => isServiceActive(s)));

        let creatorsList = [];
        try {
          const creatorsRes = await api.get("/api/auth/creators");
          creatorsList = Array.isArray(creatorsRes.data) ? creatorsRes.data : [];
        } catch (err) {
          console.warn("Route /api/auth/creators indisponible, fallback sur la liste dérivée des services.", err);
        }

        setUsers(creatorsList);
      } catch (err) {
        console.error("Erreur chargement créateurs:", err);
        setUsers([]);
      } finally {
        setLoading(false);
      }
    };

    load();
  }, []);

  const creators = useMemo(() => {
    const byCreator = new Map();

    users
      .filter((u) => u?.role === "createur")
      .forEach((creator) => {
        const creatorId = creator._id || creator.id;
        if (!creatorId) return;

        if (!byCreator.has(creatorId)) {
          byCreator.set(creatorId, {
            _id: creatorId,
            prenom: creator.prenom || "",
            name: creator.name || "",
            photo: creator.photo || "",
            metier: creator.metier || "Créateur",
            tags: Array.isArray(creator.domaine) ? creator.domaine.slice(0, 3) : [],
            categories: new Set(),
          });
        }
      });

    services.forEach((service) => {
      const creator = service.createur;
      if (!creator?._id) return;

      if (!byCreator.has(creator._id)) {
        byCreator.set(creator._id, {
          _id: creator._id,
          prenom: creator.prenom || "",
          name: creator.name || "",
          photo: creator.photo || "",
          metier: creator.metier || "Créateur",
          tags: Array.isArray(creator.domaine) ? creator.domaine.slice(0, 3) : [],
          categories: new Set(),
        });
      }

      const current = byCreator.get(creator._id);
      (service.categories || []).forEach((c) => current.categories.add(c));
      if (!current.metier && creator.metier) current.metier = creator.metier;
    });

    return Array.from(byCreator.values()).map((c) => ({
      ...c,
      tags: c.tags.length > 0 ? c.tags : Array.from(c.categories).slice(0, 3),
    }));
  }, [services, users]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return creators;
    return creators.filter((c) => {
      const fullName = `${c.prenom} ${c.name}`.toLowerCase();
      const tags = (c.tags || []).join(" ").toLowerCase();
      const metier = (c.metier || "").toLowerCase();
      return fullName.includes(q) || tags.includes(q) || metier.includes(q);
    });
  }, [creators, search]);

  return (
    <div className="min-h-screen" style={{ backgroundColor: 'var(--app-background)' }}>
      <Header />

      <main className="max-w-7xl mx-auto p-6">
        <div className="bg-white rounded-xl shadow-lg p-6 mb-8">
          <div className="flex items-center justify-between gap-4 flex-wrap">
            <div>
              <h1 className="text-2xl font-bold text-[var(--accent-primary)]">Liste des Créateurs</h1>
              <p className="text-gray-600 mt-1">Découvrez les profils des Créateurs Singuliers</p>
            </div>
            <button onClick={() => navigate(-1)} className="link-fuchsia">← Retour</button>
          </div>

          <div className="mt-4">
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Rechercher un créateur, une expérience, un tag…"
              className="w-full border px-4 py-2 rounded focus:outline-none focus:ring-2 focus:ring-[var(--accent-primary)]"
            />
          </div>
        </div>

        {loading ? (
          <p className="text-gray-600">Chargement...</p>
        ) : filtered.length === 0 ? (
          <p className="text-gray-500">Aucun créateur disponible pour le moment.</p>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
            {filtered.map((creator) => (
              <div
                key={creator._id}
                onClick={() => navigate(`/users/${creator._id}`)}
                className="bg-white rounded-xl shadow hover:shadow-lg transition cursor-pointer p-5"
              >
                <div className="flex items-center gap-3 mb-3">
                  <UserAvatar
                    photo={creator.photo}
                    name={creator.name}
                    prenom={creator.prenom}
                    className="w-14 h-14"
                  />
                  <div>
                    <h3 className="font-semibold text-[var(--accent-primary)]">{creator.prenom} {creator.name}</h3>
                    <p className="text-sm text-gray-600">{creator.metier || "Créateur"}</p>
                  </div>
                </div>

                <div className="flex flex-wrap gap-2 mt-2">
                  {(creator.tags || []).slice(0, 3).map((tag, i) => (
                    <span
                      key={`${creator._id}-${i}`}
                      className="rounded-full border border-[var(--accent-primary)] bg-[var(--accent-primary-soft)] px-2 py-1 text-xs font-medium text-[var(--accent-primary)]"
                    >
                      {tag}
                    </span>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </main>

      <Footer />
    </div>
  );
};

export default CreatorsList;
